import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../../../utils/axios";

const initialState = {
    userBookings: [],
    userReviews: [],
    loading: true,
}

const eventBookingsSlice = createSlice({
    name: "eventBookings",
    initialState,
    reducers: {},
    extraReducers: (builder) => {

        builder
            .addCase(getUserEventBookingsByUserId.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUserEventBookingsByUserId.fulfilled, (state, action) => {
                state.loading = false;
                state.userBookings = action.payload?.data || [];
                // console.log('Fetched User Event Bookings:', state.userBookings);
            })
            .addCase(getUserEventBookingsByUserId.rejected, (state) => {
                state.loading = false;
            })
    }
});

export const { } = eventBookingsSlice.actions;

export default eventBookingsSlice.reducer;


/**
 * @async
 * @function getUserEventBookingsByUserId
 * @description Thunk to fetch user event bookings by user ID by making a GET request.
 * @param {string} userId - The ID of the user whose event bookings are to be fetched.
 * @returns {Object} - The response data or error information.
 * @example
 * dispatch(getUserEventBookingsByUserId('userId123'))
 */
export const getUserEventBookingsByUserId = createAsyncThunk(
    'eventBookings/getUserEventBookingsByUserId',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v2/bookings/user/${userId}`);

            return {
                ...response.data,
                code: response.status
            }
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
            });
        }
    }
);


/**
 * @async
 * @function getUserReviewsByUserId
 * @description Thunk to fetch user reviews by user ID by making a GET request.
 * @param {string} userId - The ID of the user whose reviews are to be fetched.
 * @returns {Object} - The response data or error information.
 * 
 * @example
 * dispatch(getUserReviewsByUserId('userId123'))
 */
export const getUserReviewsByUserId = createAsyncThunk(
    'eventBookings/getUserReviewsByUserId',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v2/reviews/user/${userId}`);

            return {
                ...response.data,
                code: response.status
            }
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
            });
        }
    }
);


/**
 * @async
 * @function deleteReviewById
 * @description Thunk to delete a review by its ID by making a DELETE request.
 * @param {string} id - The ID of the review to delete.
 * @returns {Object} - The response data or error information.
 * @example
 * dispatch(deleteEventReviewById('reviewId123'))
 */
export const deleteEventReviewById = createAsyncThunk(
    'eventBookings/deleteEventReviewById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`/api/v2/reviews/${id}`);

            return {
                ...response.data,
                code: response.status
            }
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
            });
        }
    }
);


/**
 * @async
 * @function createOrUpdateOrDismissReview
 * @description Thunk to create, update, or dismiss a review by making a POST request.
 * @param {Object} payload - The payload containing review details.
 * @returns {Object} - The response data or error information.
 * @payload for create: { event_id, rating, feedback, user_id }
 * @paylod for update: { event_id, rating, feedback, user_id }
 * @payload for dismiss: { event_id, user_id, dismissed: true/false }
 */
export const createOrUpdateOrDismissReview = createAsyncThunk(
    'eventBookings/createOrUpdateOrDismissReview',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post('/api/v2/reviews', payload);

            return {
                ...response.data,
                code: response.status
            }
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
            });
        }
    }
);